/* eslint-disable */

/**
 * Phase 67 + 87ah — Détail / édition d'un devis traiteur événementiel.
 * Route : /devis/:id
 *
 * Phase 87ah (Refactor souplesse 17/06/2026)
 * - Lignes typées : PRODUCT, FREE, SECTION (titre), NOTE (encart)
 * - Toggle "Optionnel" par ligne (hors total)
 * - Champ "Notes ligne" (visible sous la description, italique)
 * - Réordonnancement (flèches ↑ ↓)
 * - Section "Frais additionnels" (livraison, service, déplacement)
 * - Totaux temps réel respectant le backend (_compute_totals)
 */
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { sortByName } from "@/lib/utils";
import { isoToLocalInput, localInputToIso, isoToLocalDate } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft, Save, Trash2, Plus, FileText, FileSignature, Download,
  Loader2, CheckCircle2, XCircle, Calendar, MapPin, Users, Send,
  Percent, Heading2, StickyNote, Truck, ArrowUp, ArrowDown, Sparkles,
} from "lucide-react";
import { pdf } from "@react-pdf/renderer";
import { CateringQuotePdf } from "@/components/CateringQuotePdf";
import { useConfirm } from "@/components/ConfirmDialog";
import { AddressBookPicker } from "@/components/AddressBookPicker";

const STATUS_META = {
  BROUILLON: { label: "Brouillon", cls: "bg-[#F4F4F0] text-[#575A57] border-[#E5E5E0]" },
  ENVOYE: { label: "Envoyé", cls: "bg-[#EAF1F8] text-[#2E5C8A] border-[#C5D9E8]" },
  ACCEPTE: { label: "Accepté", cls: "bg-[#EAF4EE] text-[#1F4E3D] border-[#C5DBCF]" },
  REFUSE: { label: "Refusé", cls: "bg-[#FCEEEA] text-[#C05C47] border-[#E8C1B4]" },
  EXPIRE: { label: "Expiré", cls: "bg-[#F0F0EB] text-[#737373] border-[#E5E5E0]" },
  ANNULE: { label: "Annulé", cls: "bg-[#F0F0EB] text-[#737373] border-[#E5E5E0]" },
  CONVERTI: { label: "Facturé", cls: "bg-[#FBF4E9] text-[#8C6B2F] border-[#E8D9B8]" },
};

const STATUS_TRANSITIONS = {
  BROUILLON: ["ENVOYE", "ANNULE"],
  ENVOYE: ["ACCEPTE", "REFUSE", "EXPIRE", "ANNULE"],
  ACCEPTE: ["CONVERTI", "ANNULE"],
  // Phase 87bk — Permettre la réouverture d'un devis ANNULÉ par erreur.
  // ACCEPTE = raccourci pour "défaire l'annulation" et retrouver le bouton convert.
  // BROUILLON = repartir du début si on veut tout refaire.
  REFUSE: ["BROUILLON"], EXPIRE: ["BROUILLON", "ENVOYE"], ANNULE: ["BROUILLON", "ACCEPTE"], CONVERTI: [],
};

const LINE_TYPE_META = {
  PRODUCT: { label: "Produit", cls: "bg-[#EAF4EE] text-[#1F4E3D] border-[#C5DBCF]" },
  FREE: { label: "Libre", cls: "bg-[#FBF4E9] text-[#8C6B2F] border-[#E8D9B8]" },
  SECTION: { label: "Section", cls: "bg-[#FDF3E0] text-[#8C6B2F] border-[#C1A063]" },
  NOTE: { label: "Note", cls: "bg-[#F4F4F0] text-[#575A57] border-[#E5E5E0]" },
};

const fmtCHF = (n) => `${(Number(n) || 0).toFixed(2)} CHF`;
const tmpId = () => `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export default function CateringQuoteDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const confirm = useConfirm();

  const [quote, setQuote] = useState(null);
  const [products, setProducts] = useState([]);
  const [cateringItems, setCateringItems] = useState([]);   // Phase 101er — catalogue traiteur
  const [settings, setSettings] = useState({});
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [linkedContact, setLinkedContact] = useState(null);  // Phase 101df-v2

  const load = async () => {
    try {
      const [q, p, ci, s] = await Promise.all([
        api.get(`/catering-quotes/${id}`),
        api.get("/products").catch(() => ({ data: [] })),
        api.get("/catering-menu-items?include_archived=false").catch(() => ({ data: [] })),
        api.get("/settings").catch(() => ({ data: {} })),
      ]);
      // Defensive : assure que les champs nouveaux existent côté front (legacy)
      const data = q.data || {};
      data.lines = (data.lines || []).map((ln) => ({
        line_type: ln.line_type || "FREE",
        optional: !!ln.optional,
        line_notes: ln.line_notes || "",
        ...ln,
      }));
      data.additional_fees = data.additional_fees || [];
      setQuote(data);
      setProducts(p.data || []);
      setCateringItems(ci.data || []);
      setSettings(s.data || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur chargement");
      navigate("/devis");
    }
  };

  useEffect(() => { load(); }, [id]);

  const isLocked = quote?.status === "CONVERTI";

  const update = (patch) => setQuote((q) => ({ ...q, ...patch }));

  // Phase 98w-fix (09/07/2026) — Persiste le devis. `silent=true` : pas de toast
  // (utilisé par l'auto-save avant conversion pour éviter le double message).
  const persistQuote = async ({ silent = false } = {}) => {
    // Nettoie les ids temporaires des lignes / fees avant envoi (laisse backend regénérer)
    const sanitizeLines = (quote.lines || []).map((ln) => ({
      ...ln,
      id: ln.id && !String(ln.id).startsWith("tmp-") ? ln.id : undefined,
    }));
    const sanitizeFees = (quote.additional_fees || []).map((f) => ({
      ...f,
      id: f.id && !String(f.id).startsWith("tmp-") ? f.id : undefined,
    }));
    await api.put(`/catering-quotes/${id}`, {
      client_name: quote.client_name,
      client_company: quote.client_company,
      client_email: quote.client_email,
      client_phone: quote.client_phone,
      client_address: quote.client_address,
      client_postal_code: quote.client_postal_code,
      client_city: quote.client_city,
      client_country: quote.client_country,
      event_date: quote.event_date,
      event_location: quote.event_location,
      event_guests_count: quote.event_guests_count ? Number(quote.event_guests_count) : null,
      event_service_type: quote.event_service_type,
      validity_until: quote.validity_until,
      notes: quote.notes,
      discount_percent: quote.discount_percent ? Number(quote.discount_percent) : null,
      discount_label: quote.discount_label,
      // Phase 98w-fix — Le toggle TVA n'était pas envoyé, donc jamais persisté.
      // Résultat : à la conversion, la facture reprenait toujours le mode DB (TTC par défaut)
      // au lieu du mode choisi par l'utilisateur dans l'UI.
      prices_include_vat: quote.prices_include_vat !== false,
      lines: sanitizeLines,
      additional_fees: sanitizeFees,
    });
    if (!silent) toast.success("Devis enregistré");
  };

  const saveAll = async () => {
    setSaving(true);
    try {
      await persistQuote();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = async (newStatus) => {
    const accepted_by = newStatus === "ACCEPTE" ? window.prompt("Nom de la personne qui a accepté le devis :") : null;
    if (newStatus === "ACCEPTE" && !accepted_by) return;
    try {
      await api.put(`/catering-quotes/${id}`, { status: newStatus, accepted_by });
      toast.success(`Statut → ${STATUS_META[newStatus].label}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const convertToInvoice = async () => {
    const ok = await confirm({
      title: "Convertir en facture ?",
      description: "Une facture brouillon sera créée avec toutes les lignes + adresse + TVA. Vous pourrez la modifier puis l'envoyer depuis la page Facture.",
      confirmText: "Convertir",
    });
    if (!ok) return;
    try {
      // Phase 98w-fix (09/07/2026) — Auto-save des modifs en cours AVANT la conversion.
      // Sans ce save, un toggle TVA/adresse édité mais non enregistré était perdu :
      // la facture reprenait la version DB périmée (CP+ville manquants, TVA remise à TTC).
      await persistQuote({ silent: true });
      const { data } = await api.post(`/catering-quotes/${id}/convert-to-invoice`);
      toast.success(`Facture ${data.invoice_reference} créée`);
      navigate(`/factures/${data.invoice_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur conversion");
    }
  };

  const deleteQuote = async () => {
    const ok = await confirm({
      title: "Supprimer définitivement ce devis ?",
      description: "Action irréversible. Seuls les devis BROUILLON peuvent être supprimés.",
      confirmText: "Supprimer",
      variant: "destructive",
    });
    if (!ok) return;
    try {
      await api.delete(`/catering-quotes/${id}`);
      toast.success("Devis supprimé");
      navigate("/devis");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  // === Lignes ===
  const addLine = (type) => {
    const newLine = {
      id: tmpId(),
      line_type: type,
      product_id: null,
      product_sku: null,
      description: type === "PRODUCT" ? "" : (type === "SECTION" ? "NOUVELLE SECTION" : type === "NOTE" ? "Note libre…" : "Prestation libre"),
      line_notes: "",
      quantity: type === "PRODUCT" || type === "FREE" ? 1 : 0,
      unit: type === "PRODUCT" ? "pcs" : (type === "FREE" ? "h" : ""),
      unit_price: 0,
      vat_rate: type === "PRODUCT" ? 2.6 : 8.1,
      optional: false,
      sort_index: (quote.lines || []).length,
    };
    update({ lines: [...(quote.lines || []), newLine] });
  };

  const updateLine = (idx, patch) => {
    const newLines = (quote.lines || []).map((ln, i) => i === idx ? { ...ln, ...patch } : ln);
    update({ lines: newLines });
  };

  const removeLine = (idx) => {
    update({ lines: (quote.lines || []).filter((_, i) => i !== idx) });
  };

  const moveLine = (idx, dir) => {
    const lines = [...(quote.lines || [])];
    const target = idx + dir;
    if (target < 0 || target >= lines.length) return;
    [lines[idx], lines[target]] = [lines[target], lines[idx]];
    lines.forEach((ln, i) => { ln.sort_index = i; });
    update({ lines });
  };

  const onSelectProduct = (idx, productId) => {
    // Phase 101er — Priorité au catalogue traiteur (`cateringItems`),
    // fallback sur `products` (barquettes). Un produit picked ici met
    // à jour la ligne du devis avec un prix suggéré modifiable.
    const catering = cateringItems.find((c) => c.id === productId);
    if (catering) {
      updateLine(idx, {
        product_id: catering.id,
        product_sku: catering.sku || null,
        description: catering.name,
        unit: catering.portion_unit || "pcs",
        unit_price: catering.default_price ?? 0,
        vat_rate: catering.vat_rate ?? 2.6,
        // Phase 101er — Buffet : injecte la composition dans les notes ligne
        // (visible sur le PDF cuisine à venir + rappel à l'écran)
        line_notes: (catering.category === "buffet" && (catering.components || []).length > 0)
          ? "Contenu : " + catering.components
              .map((c) => {
                const it = cateringItems.find((x) => x.id === c.item_id);
                return `${c.quantity}× ${it?.name || "?"}`;
              })
              .join(" · ")
          : (catering.description || ""),
      });
      return;
    }
    const p = products.find((p) => p.id === productId);
    if (!p) return;
    // Legacy fallback — Phase 101du.7 : price_traiteur > price_direct
    const suggestedPrice = p.price_traiteur ?? p.price_direct ?? p.menu_price ?? 0;
    updateLine(idx, {
      product_id: p.id,
      product_sku: p.sku,
      description: p.name,
      unit: p.unit || "pcs",
      unit_price: suggestedPrice,
      vat_rate: p.vat_rate ?? 2.6,
    });
  };

  // === Frais additionnels ===
  const addFee = () => {
    update({
      additional_fees: [
        ...(quote.additional_fees || []),
        { id: tmpId(), description: "Livraison", amount: 0, vat_rate: 8.1 },
      ],
    });
  };
  const updateFee = (idx, patch) => {
    const fees = (quote.additional_fees || []).map((f, i) => i === idx ? { ...f, ...patch } : f);
    update({ additional_fees: fees });
  };
  const removeFee = (idx) => {
    update({ additional_fees: (quote.additional_fees || []).filter((_, i) => i !== idx) });
  };

  // === Totaux ===
  // Phase 98w — Deux modes TVA :
  // - `prices_include_vat=true` (défaut traiteur privé) : unit_price = TTC, total = somme brute
  // - `prices_include_vat=false` (traiteur B2B) : unit_price = HT, TVA ajoutée
  const totals = useMemo(() => {
    const lines = quote?.lines || [];
    const fees = quote?.additional_fees || [];
    const priceIncludesVat = quote?.prices_include_vat !== false;  // défaut true
    let totalLineAmount = 0;
    const vatGroups = {};
    lines.forEach((ln) => {
      if (ln.line_type === "SECTION" || ln.line_type === "NOTE") return;
      if (ln.optional) return;
      const lineAmount = Number(ln.quantity || 0) * Number(ln.unit_price || 0);
      totalLineAmount += lineAmount;
      const r = Number(ln.vat_rate || 0);
      vatGroups[r] = (vatGroups[r] || 0) + lineAmount;
    });
    const discountPct = Number(quote?.discount_percent || 0);
    const discount = discountPct > 0 ? totalLineAmount * discountPct / 100 : 0;
    if (discountPct > 0) {
      Object.keys(vatGroups).forEach((k) => { vatGroups[k] *= (1 - discountPct / 100); });
    }
    let feesTotal = 0;
    fees.forEach((f) => {
      const amount = Number(f.amount || 0);
      if (!amount) return;
      feesTotal += amount;
      const r = Number(f.vat_rate || 0);
      vatGroups[r] = (vatGroups[r] || 0) + amount;
    });
    const totalAfter = totalLineAmount - discount + feesTotal;
    let vatTotal = 0;
    const vatByRate = {};
    let ht;
    let ttc;
    if (priceIncludesVat) {
      // Mode TTC-inclus : le total est le TTC. Extrait le HT + TVA pour info.
      ttc = totalAfter;
      Object.entries(vatGroups).forEach(([r, base]) => {
        const rate = Number(r);
        const v = rate ? base - base / (1 + rate / 100) : 0;
        vatTotal += v;
        if (base > 0 && rate) vatByRate[r] = v;
      });
      ht = ttc - vatTotal;
    } else {
      // Mode HT + TVA ajoutée : comportement historique.
      ht = totalAfter;
      Object.entries(vatGroups).forEach(([r, base]) => {
        const v = base * Number(r) / 100;
        vatTotal += v;
        if (base > 0) vatByRate[r] = v;
      });
      ttc = ht + vatTotal;
    }
    return {
      ht_initial: totalLineAmount,
      discount,
      fees_total_ht: feesTotal,
      ht,
      vat: vatTotal,
      ttc,
      vatGroups: vatByRate,
      prices_include_vat: priceIncludesVat,
    };
  }, [quote]);

  const downloadPdf = async (mode) => {
    setGenerating(true);
    try {
      const blob = await pdf(<CateringQuotePdf quote={quote} settings={settings} totals={totals} mode={mode} />).toBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const labels = { OFFER: "Offre", CONFIRMATION: "Confirmation", INVOICE: "Facture" };
      a.download = `${labels[mode] || mode}-${quote.reference}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast.error("Erreur génération PDF: " + e.message);
    } finally {
      setGenerating(false);
    }
  };

  if (!quote) return <div className="flex items-center gap-2 text-sm text-[#575A57]"><Loader2 className="w-4 h-4 animate-spin" />Chargement…</div>;

  const meta = STATUS_META[quote.status] || STATUS_META.BROUILLON;
  const transitions = STATUS_TRANSITIONS[quote.status] || [];

  return (
    <div className="space-y-6" data-testid="catering-quote-detail">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <Link to="/devis" className="text-[#575A57] hover:text-[#1A1C1A] mt-1"><ArrowLeft className="w-5 h-5" /></Link>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl font-mono font-semibold">{quote.reference}</h1>
              <Badge className={`${meta.cls} uppercase text-[10px] tracking-wider`}>{meta.label}</Badge>
              {quote.invoice_id && (
                <Link to={`/factures/${quote.invoice_id}`}>
                  <Badge className="bg-[#FBF4E9] text-[#8C6B2F] border-[#E8D9B8] text-[10px]"><FileText className="w-3 h-3 mr-1" />Voir la facture</Badge>
                </Link>
              )}
            </div>
            <p className="text-xs text-[#A3A6A3] mt-1">Créé le {new Date(quote.created_at).toLocaleDateString("fr-CH")}</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {!isLocked && (
            <Button size="sm" onClick={saveAll} disabled={saving} className="bg-[#1F4E3D] hover:bg-[#173B2E]" data-testid="catering-quote-save">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
              Enregistrer
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={() => downloadPdf("OFFER")} disabled={generating} data-testid="catering-quote-pdf-offer">
            <Download className="w-3.5 h-3.5 mr-1.5" /> Offre
          </Button>
          {(quote.status === "ACCEPTE" || quote.status === "CONVERTI") && (
            <Button size="sm" variant="outline" onClick={() => downloadPdf("CONFIRMATION")} disabled={generating} data-testid="catering-quote-pdf-confirmation">
              <Download className="w-3.5 h-3.5 mr-1.5" /> Confirmation
            </Button>
          )}
          {quote.status === "ACCEPTE" && (
            <Button size="sm" onClick={convertToInvoice} className="bg-[#8C6B2F] hover:bg-[#735524] text-white" data-testid="catering-quote-convert">
              <FileSignature className="w-3.5 h-3.5 mr-1.5" /> Convertir en facture
            </Button>
          )}
          {/* Phase 87bk — Annuler la conversion (utile si facture orpheline avec
              creditor vides à cause du bug de la collection settings) */}
          {quote.status === "CONVERTI" && (
            <Button
              size="sm"
              variant="outline"
              onClick={async () => {
                const ok = await confirm({
                  title: "Annuler la conversion ?",
                  description: "La facture liée sera supprimée et le devis repassera en ACCEPTÉ. Tu pourras ensuite reconvertir.",
                  confirmText: "Oui, annuler la conversion",
                  destructive: true,
                });
                if (!ok) return;
                try {
                  await api.post(`/catering-quotes/${id}/undo-conversion`);
                  toast.success("Conversion annulée. Le devis est repassé en ACCEPTÉ. Tu peux maintenant cliquer « Convertir en facture ».");
                  await load();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Erreur");
                }
              }}
              className="border-[#C05C47] text-[#C05C47] hover:bg-[#FCEEEA]"
              data-testid="catering-quote-undo-conversion"
              title="Supprime la facture liée et remet le devis en ACCEPTÉ pour pouvoir reconvertir"
            >
              <XCircle className="w-3.5 h-3.5 mr-1.5" /> Annuler la conversion
            </Button>
          )}
          {quote.status === "BROUILLON" && (
            <Button size="sm" variant="outline" onClick={deleteQuote} className="border-[#C05C47] text-[#C05C47] hover:bg-[#FCEEEA]" data-testid="catering-quote-delete">
              <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Supprimer
            </Button>
          )}
        </div>
      </div>

      {/* Transitions de statut */}
      {transitions.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-[#575A57]">Changer le statut :</span>
          {transitions.filter((s) => s !== "CONVERTI").map((s) => (
            <Button
              key={s}
              size="sm"
              variant="outline"
              onClick={() => changeStatus(s)}
              className="text-xs"
              data-testid={`catering-quote-status-${s}`}
            >
              {s === "ENVOYE" && <Send className="w-3 h-3 mr-1" />}
              {s === "ACCEPTE" && <CheckCircle2 className="w-3 h-3 mr-1" />}
              {(s === "REFUSE" || s === "ANNULE") && <XCircle className="w-3 h-3 mr-1" />}
              {STATUS_META[s].label}
            </Button>
          ))}
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-4">
        {/* CLIENT */}
        <Card className="border-[#E5E5E0]">
          <CardContent className="p-5 space-y-3">
            <div className="flex items-center justify-between gap-2">
              <h2 className="font-semibold text-sm uppercase tracking-wider text-[#1F4E3D]">Client</h2>
              {/* Phase 101df — Sélection depuis carnet d'adresses centralisé
                  Phase 101df-v2 — Chip contact lié + mini-carte au survol */}
              {!isLocked && (
                <AddressBookPicker
                  testid="catering-quote-address-book-picker"
                  triggerLabel="Depuis le carnet"
                  preferredRoles={["TRAITEUR", "REVENDEUR", "COURRIER"]}
                  linkedContact={linkedContact}
                  onUnlink={() => setLinkedContact(null)}
                  onSelect={(c) => {
                    update({
                      client_name: c.name || "",
                      client_company: c.organization || "",
                      client_phone: c.phone || "",
                      client_email: c.email || "",
                      client_address: c.address || "",
                      client_postal_code: c.postal_code || "",
                      client_city: c.city || "",
                    });
                    setLinkedContact(c);
                    toast.success("Client importé depuis le carnet");
                  }}
                />
              )}
            </div>
            <Field label="Nom du contact *" value={quote.client_name} onChange={(v) => update({ client_name: v })} disabled={isLocked} testid="client-name" />
            <Field label="Société" value={quote.client_company} onChange={(v) => update({ client_company: v })} disabled={isLocked} testid="client-company" />
            <div className="grid grid-cols-2 gap-3">
              <Field label="Téléphone" value={quote.client_phone} onChange={(v) => update({ client_phone: v })} disabled={isLocked} testid="client-phone" />
              <Field label="Email" type="email" value={quote.client_email} onChange={(v) => update({ client_email: v })} disabled={isLocked} testid="client-email" />
            </div>
            <Field label="Adresse (rue)" value={quote.client_address} onChange={(v) => update({ client_address: v })} disabled={isLocked} testid="client-address" />
            <div className="grid grid-cols-3 gap-3">
              <Field label="NPA" value={quote.client_postal_code} onChange={(v) => update({ client_postal_code: v })} disabled={isLocked} testid="client-postal" />
              <Field label="Ville" value={quote.client_city} onChange={(v) => update({ client_city: v })} disabled={isLocked} testid="client-city" wrapperClass="col-span-2" />
            </div>
          </CardContent>
        </Card>

        {/* EVENT */}
        <Card className="border-[#E5E5E0]">
          <CardContent className="p-5 space-y-3">
            <h2 className="font-semibold text-sm uppercase tracking-wider text-[#1F4E3D] flex items-center gap-1.5">
              <Calendar className="w-4 h-4" /> Événement
            </h2>
            <Field label="Date & heure" type="datetime-local" value={isoToLocalInput(quote.event_date)} onChange={(v) => update({ event_date: localInputToIso(v) })} disabled={isLocked} testid="event-date" />
            <Field label="Lieu" value={quote.event_location} onChange={(v) => update({ event_location: v })} disabled={isLocked} testid="event-location" icon={MapPin} />
            <div className="grid grid-cols-2 gap-3">
              <Field label="Nb personnes" type="number" value={quote.event_guests_count || ""} onChange={(v) => update({ event_guests_count: v })} disabled={isLocked} testid="event-guests" icon={Users} />
              <Field label="Type de service" value={quote.event_service_type} onChange={(v) => update({ event_service_type: v })} disabled={isLocked} testid="event-service" placeholder="Buffet, service, à emporter…" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* LIGNES */}
      <Card className="border-[#E5E5E0]">
        <CardContent className="p-5">
          <div className="flex items-center justify-between gap-2 flex-wrap mb-4">
            <h2 className="font-semibold text-sm uppercase tracking-wider text-[#1F4E3D]">Lignes du devis</h2>
            {!isLocked && (
              <div className="flex gap-2 flex-wrap">
                <Button size="sm" variant="outline" onClick={() => addLine("PRODUCT")} data-testid="add-product-line">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Produit catalogue
                </Button>
                <Button size="sm" variant="outline" onClick={() => addLine("FREE")} data-testid="add-free-line">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Ligne libre
                </Button>
                <Button size="sm" variant="outline" onClick={() => addLine("SECTION")} className="border-[#C1A063] text-[#8C6B2F] hover:bg-[#FDF3E0]" data-testid="add-section-line">
                  <Heading2 className="w-3.5 h-3.5 mr-1" /> Titre de section
                </Button>
                <Button size="sm" variant="outline" onClick={() => addLine("NOTE")} className="border-[#575A57] text-[#575A57] hover:bg-[#F4F4F0]" data-testid="add-note-line">
                  <StickyNote className="w-3.5 h-3.5 mr-1" /> Note
                </Button>
              </div>
            )}
          </div>

          {(!quote.lines || quote.lines.length === 0) && (
            <p className="text-sm text-[#A3A6A3] text-center py-4" data-testid="catering-lines-empty">
              Aucune ligne. Ajoutez un produit, une prestation libre, un titre de section ou une note.
            </p>
          )}

          <div className="space-y-2">
            {(quote.lines || []).map((ln, idx) => (
              <LineEditor
                key={ln.id || idx}
                line={ln}
                idx={idx}
                total={(quote.lines || []).length}
                products={products}
                cateringItems={cateringItems}
                isLocked={isLocked}
                onUpdate={(patch) => updateLine(idx, patch)}
                onSelectProduct={(pid) => onSelectProduct(idx, pid)}
                onRemove={() => removeLine(idx)}
                onMove={(dir) => moveLine(idx, dir)}
              />
            ))}
          </div>
        </CardContent>
      </Card>

      {/* FRAIS ADDITIONNELS */}
      <Card className="border-[#E5E5E0]">
        <CardContent className="p-5">
          <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
            <h2 className="font-semibold text-sm uppercase tracking-wider text-[#8C6B2F] flex items-center gap-1.5">
              <Truck className="w-4 h-4" /> Frais additionnels
            </h2>
            {!isLocked && (
              <Button size="sm" variant="outline" onClick={addFee} className="border-[#8C6B2F] text-[#8C6B2F] hover:bg-[#FBF4E9]" data-testid="add-fee">
                <Plus className="w-3.5 h-3.5 mr-1" /> Ajouter un frais
              </Button>
            )}
          </div>

          {(quote.additional_fees || []).length === 0 ? (
            <p className="text-xs text-[#A3A6A3]" data-testid="catering-fees-empty">
              Aucun frais. Ajoutez la livraison, le déplacement, le service, etc.
            </p>
          ) : (
            <div className="space-y-2">
              {(quote.additional_fees || []).map((fee, idx) => (
                <div key={fee.id || idx} className="grid grid-cols-12 gap-2 items-center bg-[#FBF4E9] border border-[#E8D9B8] rounded p-2" data-testid={`catering-fee-${idx}`}>
                  <div className="col-span-12 md:col-span-7">
                    <Input
                      value={fee.description || ""}
                      onChange={(e) => updateFee(idx, { description: e.target.value })}
                      placeholder="Description (ex: Livraison Sion → Genève)"
                      disabled={isLocked}
                      data-testid={`fee-desc-${idx}`}
                    />
                  </div>
                  <div className="col-span-6 md:col-span-2">
                    <div className="relative">
                      <Input
                        type="number"
                        step="0.05"
                        value={fee.amount}
                        onChange={(e) => updateFee(idx, { amount: Number(e.target.value) })}
                        disabled={isLocked}
                        className="text-right pr-12"
                        data-testid={`fee-amount-${idx}`}
                      />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-[#A3A6A3]">CHF</span>
                    </div>
                  </div>
                  <div className="col-span-4 md:col-span-2">
                    <select
                      value={fee.vat_rate}
                      onChange={(e) => updateFee(idx, { vat_rate: Number(e.target.value) })}
                      disabled={isLocked}
                      className="w-full border border-[#E5E5E0] rounded px-2 py-1.5 text-xs bg-white disabled:bg-[#F4F4F0]"
                      data-testid={`fee-vat-${idx}`}
                    >
                      <option value={0}>TVA 0%</option>
                      <option value={2.6}>TVA 2.6%</option>
                      <option value={3.8}>TVA 3.8%</option>
                      <option value={8.1}>TVA 8.1%</option>
                    </select>
                  </div>
                  {!isLocked && (
                    <button
                      onClick={() => removeFee(idx)}
                      className="col-span-2 md:col-span-1 text-[#C05C47] hover:bg-[#FCEEEA] rounded p-1 inline-flex justify-center"
                      data-testid={`fee-remove-${idx}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bottom : remise + notes + totaux */}
      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="border-[#E5E5E0] lg:col-span-2">
          <CardContent className="p-5 space-y-3">
            <h2 className="font-semibold text-sm uppercase tracking-wider text-[#1F4E3D]">Conditions & remise</h2>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Validité jusqu'au" type="date" value={isoToLocalDate(quote.validity_until)} onChange={(v) => update({ validity_until: v || null })} disabled={isLocked} testid="validity" />
              <Field label="Remise globale (%)" type="number" value={quote.discount_percent || ""} onChange={(v) => update({ discount_percent: v ? Number(v) : null })} disabled={isLocked} testid="discount-pct" icon={Percent} />
            </div>
            <Field label="Libellé remise" value={quote.discount_label || ""} onChange={(v) => update({ discount_label: v })} disabled={isLocked} testid="discount-label" placeholder="Ex. Remise fidélité, première commande…" />
            {/* Phase 98w — Toggle mode TVA
                Phase 98w-fix (09/07/2026) — Bascule les prix unitaires en même temps
                pour PRÉSERVER le total TTC (cas typique : client a accepté 300 CHF TTC,
                on doit refacturer 300 CHF TTC mais en mode HT + TVA). */}
            <label className="flex items-start gap-2 pt-1 cursor-pointer" data-testid="quote-vat-mode-toggle">
              <input
                type="checkbox"
                checked={quote.prices_include_vat !== false}
                onChange={(e) => {
                  const nextMode = e.target.checked;  // true = TTC-inclus, false = HT
                  const wasMode = quote.prices_include_vat !== false;
                  // Convertit les unit_prices pour préserver le total TTC :
                  // - Was TTC (unit_price=TTC) → Now HT : divise par (1+rate)
                  // - Was HT (unit_price=HT) → Now TTC : multiplie par (1+rate)
                  const convertedLines = (quote.lines || []).map((ln) => {
                    const rate = Number(ln.vat_rate || 0);
                    if (!rate || wasMode === nextMode) return ln;
                    const p = Number(ln.unit_price || 0);
                    const newPrice = wasMode
                      ? p / (1 + rate / 100)   // TTC → HT
                      : p * (1 + rate / 100);  // HT → TTC
                    return { ...ln, unit_price: Math.round(newPrice * 100) / 100 };
                  });
                  const convertedFees = (quote.additional_fees || []).map((f) => {
                    const rate = Number(f.vat_rate || 0);
                    if (!rate || wasMode === nextMode) return f;
                    const p = Number(f.amount || 0);
                    const newPrice = wasMode
                      ? p / (1 + rate / 100)
                      : p * (1 + rate / 100);
                    return { ...f, amount: Math.round(newPrice * 100) / 100 };
                  });
                  update({
                    prices_include_vat: nextMode,
                    lines: convertedLines,
                    additional_fees: convertedFees,
                  });
                }}
                disabled={isLocked}
                className="mt-0.5"
                data-testid="quote-vat-mode-checkbox"
              />
              <span className="text-[13px] leading-snug">
                <strong>Prix TTC (TVA incluse)</strong>
                <span className="block text-[11px] text-[#6B6E6B]">
                  Coché = client particulier / traiteur privé (prix affiché = TTC, TVA en petit sous le total).<br />
                  Décoché = client entreprise / B2B (prix HT + ligne TVA + total TTC).<br />
                  <em>Les prix sont automatiquement convertis pour préserver le total TTC.</em>
                </span>
              </span>
            </label>
            <label className="block">
              <span className="block text-[10px] uppercase tracking-wider text-[#575A57] mb-1">Notes / conditions particulières</span>
              <textarea
                value={quote.notes || ""}
                onChange={(e) => update({ notes: e.target.value })}
                disabled={isLocked}
                rows={4}
                className="w-full border border-[#E5E5E0] rounded p-2 text-sm bg-white disabled:bg-[#F4F4F0]"
                placeholder="Ex. Acompte 30% à la confirmation, solde à la livraison…"
                data-testid="quote-notes"
              />
            </label>
          </CardContent>
        </Card>

        <Card className="border-[#1F4E3D] border-2">
          <CardContent className="p-5">
            <h2 className="font-semibold text-sm uppercase tracking-wider text-[#1F4E3D] mb-3 flex items-center gap-1.5">
              <Sparkles className="w-4 h-4" /> Totaux
              <span className="ml-auto text-[10px] font-normal text-[#6B6E6B]">
                {totals.prices_include_vat ? "Mode TTC-inclus" : "Mode HT + TVA"}
              </span>
            </h2>
            <div className="space-y-1.5 text-sm" data-testid="catering-totals">
              {totals.discount > 0 && (
                <>
                  <Row label={`Sous-total articles ${totals.prices_include_vat ? "TTC" : "HT"}`} value={fmtCHF(totals.ht_initial)} muted />
                  <Row label={`Remise ${quote.discount_percent || 0}%`} value={`− ${fmtCHF(totals.discount)}`} color="text-[#C05C47]" />
                </>
              )}
              {totals.fees_total_ht > 0 && (
                <Row label={`Frais additionnels ${totals.prices_include_vat ? "TTC" : "HT"}`} value={`+ ${fmtCHF(totals.fees_total_ht)}`} color="text-[#8C6B2F]" />
              )}
              {totals.prices_include_vat ? (
                // Mode TTC-inclus — Total TTC prominent + « dont TVA » en petit
                <>
                  <div className="border-t border-[#1F4E3D] mt-3 pt-3">
                    <Row label="Total TTC" value={fmtCHF(totals.ttc)} bold large />
                  </div>
                  {Object.entries(totals.vatGroups || {}).map(([r, vatAmount]) => (
                    <Row key={r} label={`dont TVA ${r}%`} value={fmtCHF(vatAmount)} muted />
                  ))}
                </>
              ) : (
                // Mode HT + TVA — classique
                <>
                  <Row label="Total HT" value={fmtCHF(totals.ht)} />
                  {Object.entries(totals.vatGroups || {}).map(([r, vatAmount]) => (
                    <Row key={r} label={`TVA ${r}%`} value={fmtCHF(vatAmount)} muted />
                  ))}
                  <div className="border-t border-[#1F4E3D] mt-3 pt-3">
                    <Row label="Total TTC" value={fmtCHF(totals.ttc)} bold large />
                  </div>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

/* =============================================================
 * LineEditor — rendu adaptatif selon le line_type
 * ============================================================= */
function LineEditor({ line, idx, total, products, cateringItems = [], isLocked, onUpdate, onSelectProduct, onRemove, onMove }) {
  const type = line.line_type || "FREE";
  const typeMeta = LINE_TYPE_META[type] || LINE_TYPE_META.FREE;

  // SECTION : pleine largeur, juste un texte capitalisé
  if (type === "SECTION") {
    return (
      <div className="border-2 border-[#C1A063] rounded p-3 bg-[#FDF3E0] flex items-center gap-2" data-testid={`catering-line-${idx}`}>
        <Badge className={`${typeMeta.cls} text-[10px]`}><Heading2 className="w-3 h-3 mr-1" />{typeMeta.label}</Badge>
        <Input
          value={line.description || ""}
          onChange={(e) => onUpdate({ description: e.target.value })}
          placeholder="TITRE DE SECTION (ex: ENTRÉES, PLAT PRINCIPAL, SERVICE…)"
          disabled={isLocked}
          className="flex-1 font-semibold uppercase tracking-wider bg-white"
          data-testid={`line-desc-${idx}`}
        />
        <ReorderControls idx={idx} total={total} onMove={onMove} isLocked={isLocked} />
        {!isLocked && (
          <button onClick={onRemove} className="text-[#C05C47] hover:bg-[#FCEEEA] rounded p-1" data-testid={`line-remove-${idx}`}>
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
    );
  }

  // NOTE : pleine largeur, encart italique
  if (type === "NOTE") {
    return (
      <div className="border border-[#E5E5E0] rounded p-3 bg-[#FAFAF7] flex items-start gap-2" data-testid={`catering-line-${idx}`}>
        <Badge className={`${typeMeta.cls} text-[10px] mt-1.5`}><StickyNote className="w-3 h-3 mr-1" />{typeMeta.label}</Badge>
        <textarea
          value={line.description || ""}
          onChange={(e) => onUpdate({ description: e.target.value })}
          placeholder="Note ou remarque (allergènes à confirmer, demande spéciale, etc.)"
          disabled={isLocked}
          rows={2}
          className="flex-1 border border-[#E5E5E0] rounded p-2 text-sm italic bg-white disabled:bg-[#F4F4F0]"
          data-testid={`line-desc-${idx}`}
        />
        <div className="flex flex-col gap-1">
          <ReorderControls idx={idx} total={total} onMove={onMove} isLocked={isLocked} />
          {!isLocked && (
            <button onClick={onRemove} className="text-[#C05C47] hover:bg-[#FCEEEA] rounded p-1 inline-flex justify-center" data-testid={`line-remove-${idx}`}>
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    );
  }

  // PRODUCT / FREE
  return (
    <div className={`border rounded p-3 ${line.optional ? "bg-[#FBF4E9] border-[#E8D9B8]" : "bg-[#FAFAF8] border-[#E5E5E0]"}`} data-testid={`catering-line-${idx}`}>
      <div className="grid grid-cols-12 gap-2 items-center">
        <div className="col-span-12 md:col-span-1 flex items-center gap-1">
          <Badge className={`${typeMeta.cls} text-[10px]`}>{typeMeta.label}</Badge>
        </div>
        <div className="col-span-12 md:col-span-4">
          {type === "PRODUCT" ? (
            <select
              value={line.product_id || ""}
              onChange={(e) => onSelectProduct(e.target.value)}
              disabled={isLocked}
              className="w-full border border-[#E5E5E0] rounded px-2 py-1.5 text-sm bg-white disabled:bg-[#F4F4F0]"
              data-testid={`line-product-${idx}`}
            >
              <option value="">— Sélectionner un item —</option>
              {/* Phase 101er — Catalogue traiteur groupé par catégorie (source principale) */}
              {cateringItems.length > 0 && (
                <>
                  {[
                    { key: "entree", label: "Entrées" },
                    { key: "plat", label: "Plats" },
                    { key: "accompagnement", label: "Accompagnements" },
                    { key: "dessert", label: "Desserts" },
                    { key: "buffet", label: "Buffets composés" },
                    { key: "prestation", label: "Prestations" },
                  ].map((cat) => {
                    const catItems = cateringItems.filter((c) => c.category === cat.key && !c.archived);
                    if (catItems.length === 0) return null;
                    return (
                      <optgroup key={cat.key} label={`▸ ${cat.label} (traiteur)`}>
                        {catItems.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name}{c.sku ? ` [${c.sku}]` : ""} — {Number(c.default_price || 0).toFixed(2)} CHF/{c.portion_unit}
                          </option>
                        ))}
                      </optgroup>
                    );
                  })}
                </>
              )}
              {/* Fallback : produits barquettes (legacy — pour compat avec devis existants) */}
              <optgroup label="◇ Barquettes (catalogue général)">
                {sortByName(products.filter((p) => p.is_active !== false && !p.archived)).map((p) => (
                  <option key={p.id} value={p.id}>{p.name} ({p.sku})</option>
                ))}
              </optgroup>
            </select>
          ) : (
            <Input
              value={line.description}
              onChange={(e) => onUpdate({ description: e.target.value })}
              placeholder="Description (ex. Service serveur 4h)"
              disabled={isLocked}
              data-testid={`line-desc-${idx}`}
            />
          )}
        </div>
        <div className="col-span-3 md:col-span-1">
          <Input
            type="number"
            step="0.1"
            value={line.quantity}
            onChange={(e) => onUpdate({ quantity: Number(e.target.value) })}
            disabled={isLocked}
            className="text-right"
            data-testid={`line-qty-${idx}`}
          />
        </div>
        <div className="col-span-3 md:col-span-1">
          <Input
            value={line.unit}
            onChange={(e) => onUpdate({ unit: e.target.value })}
            disabled={isLocked}
            className="text-xs"
            data-testid={`line-unit-${idx}`}
          />
        </div>
        <div className="col-span-3 md:col-span-2">
          <div className="relative">
            <Input
              type="number"
              step="0.05"
              value={line.unit_price}
              onChange={(e) => onUpdate({ unit_price: Number(e.target.value) })}
              disabled={isLocked}
              className="text-right pr-12"
              data-testid={`line-price-${idx}`}
            />
            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-[#A3A6A3]">CHF</span>
          </div>
        </div>
        <div className="col-span-2 md:col-span-1">
          <select
            value={line.vat_rate}
            onChange={(e) => onUpdate({ vat_rate: Number(e.target.value) })}
            disabled={isLocked}
            className="w-full border border-[#E5E5E0] rounded px-1.5 py-1.5 text-xs bg-white disabled:bg-[#F4F4F0]"
            data-testid={`line-vat-${idx}`}
          >
            <option value={0}>0%</option>
            <option value={2.6}>2.6%</option>
            <option value={3.8}>3.8%</option>
            <option value={8.1}>8.1%</option>
          </select>
        </div>
        <div className={`col-span-9 md:col-span-1 text-right font-mono text-sm font-semibold ${line.optional ? "text-[#8C6B2F] line-through" : "text-[#1F4E3D]"}`}>
          {fmtCHF((line.quantity || 0) * (line.unit_price || 0))}
        </div>
        {!isLocked && (
          <div className="col-span-3 md:col-span-1 flex items-center justify-end gap-1">
            <ReorderControls idx={idx} total={total} onMove={onMove} isLocked={isLocked} />
            <button onClick={onRemove} className="text-[#C05C47] hover:bg-[#FCEEEA] rounded p-1" data-testid={`line-remove-${idx}`}>
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* Sous-ligne : optional + line_notes */}
      <div className="mt-2 grid grid-cols-12 gap-2 items-center">
        <div className="col-span-12 md:col-span-3 flex items-center gap-2">
          <label className="flex items-center gap-1.5 text-xs cursor-pointer" data-testid={`line-optional-label-${idx}`}>
            <input
              type="checkbox"
              checked={!!line.optional}
              onChange={(e) => onUpdate({ optional: e.target.checked })}
              disabled={isLocked}
              data-testid={`line-optional-${idx}`}
              className="w-3.5 h-3.5"
            />
            <span className={line.optional ? "text-[#8C6B2F] font-medium" : "text-[#575A57]"}>
              Optionnel (à confirmer)
            </span>
          </label>
        </div>
        <div className="col-span-12 md:col-span-9">
          <Input
            value={line.line_notes || ""}
            onChange={(e) => onUpdate({ line_notes: e.target.value })}
            placeholder="Note interne ou précision (ex: sans cacahuètes, dressage particulier…)"
            disabled={isLocked}
            className="text-xs italic"
            data-testid={`line-notes-${idx}`}
          />
        </div>
      </div>
    </div>
  );
}

function ReorderControls({ idx, total, onMove, isLocked }) {
  if (isLocked) return null;
  return (
    <div className="flex flex-col">
      <button
        onClick={() => onMove(-1)}
        disabled={idx === 0}
        className="text-[#575A57] hover:text-[#1F4E3D] disabled:opacity-30 disabled:cursor-not-allowed p-0.5"
        data-testid={`line-move-up-${idx}`}
        title="Monter"
      >
        <ArrowUp className="w-3 h-3" />
      </button>
      <button
        onClick={() => onMove(1)}
        disabled={idx === total - 1}
        className="text-[#575A57] hover:text-[#1F4E3D] disabled:opacity-30 disabled:cursor-not-allowed p-0.5"
        data-testid={`line-move-down-${idx}`}
        title="Descendre"
      >
        <ArrowDown className="w-3 h-3" />
      </button>
    </div>
  );
}

function Field({ label, value, onChange, disabled, type = "text", testid, placeholder, icon: Icon, wrapperClass = "" }) {
  return (
    <label className={`block ${wrapperClass}`}>
      <span className="block text-[10px] uppercase tracking-wider text-[#575A57] mb-1 flex items-center gap-1">
        {Icon ? <Icon className="w-3 h-3" /> : null}
        {label}
      </span>
      <Input
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        placeholder={placeholder}
        data-testid={testid}
      />
    </label>
  );
}

function Row({ label, value, muted, bold, large, color }) {
  return (
    <div className={`flex justify-between items-baseline ${muted ? "text-[#737373]" : ""}`}>
      <span className={bold ? "font-semibold text-[#1A1C1A]" : ""}>{label}</span>
      <span className={`font-mono ${large ? "text-lg" : ""} ${bold ? "font-semibold text-[#1F4E3D]" : ""} ${color || ""}`}>{value}</span>
    </div>
  );
}
